import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FiTrendingUp, FiClock
} from "react-icons/fi";

/* ═══════════════════════════════════════════
   VIBRANT NEUROMORPHISM STYLES — Consistent with PU&AP Dashboard
   Blue · Green · Amber · Purple · Red palette
═══════════════════════════════════════════ */
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');

  .lbbp-root {
    font-family: 'Montserrat', sans-serif;
    background: linear-gradient(135deg, #e0e5ec 0%, #d1d6df 100%);
    min-height: 100%;
    padding: 6px 5px;
  }
  .lbbp-root *, .lbbp-root *::before, .lbbp-root *::after { box-sizing: border-box; }
  
  .lbbp-root ::-webkit-scrollbar { width: 6px; height: 6px; }
  .lbbp-root ::-webkit-scrollbar-track { 
    background: #d1d6df;
    border-radius: 99px;
    box-shadow: inset 0 1px 3px rgba(0,0,0,.1);
  }
  .lbbp-root ::-webkit-scrollbar-thumb { 
    background: linear-gradient(135deg, #2563eb, #8b5cf6);
    border-radius: 99px;
    box-shadow: inset 0 1px 2px rgba(255,255,255,.3), inset 0 -1px 2px rgba(0,0,0,.1);
  }

  .lbbp-page {
    max-width: 1280px;
    margin: 0 auto;
    display: flex; flex-direction: column; gap: 28px;
  }
  @media (max-width: 600px) { .lbbp-page { gap: 20px; } }

  /* ══════════════════════════════
     HERO SECTION — Vibrant Gradient
  ══════════════════════════════ */
  .lbbp-hero {
    background: linear-gradient(135deg, #0a1628 0%, #1e3a5f 50%, #2563eb 100%);
    border-radius: 9px;
    padding: 20px 22px;
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    flex-wrap: wrap;
    box-shadow: 8px 8px 16px #8f95a0, -8px -8px 16px #ffffff;
    border: 1px solid rgba(255,255,255,0.18);
  }
  .lbbp-hero::before {
    content: '';
    position: absolute;
    top: -24%;
    right: -8%;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(59,130,246,.2) 0%, transparent 70%);
    pointer-events: none;
  }
  .lbbp-hero::after {
    content: '';
    position: absolute;
    bottom: -24%;
    left: -8%;
    width: 200px;
    height: 200px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(139,92,246,.15) 0%, transparent 70%);
    pointer-events: none;
  }
  .lbbp-hero-left { position: relative; z-index: 1; }
  .lbbp-hero-badge {
    display: inline-flex; align-items: center; gap: 6px;
    background: rgba(255,255,255,0.12);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255,255,255,0.25);
    border-radius: 99px;
    padding: 5px 12px;
    font-size: 10px;
    font-weight: 700;
    color: #fbbf24;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin-bottom: 12px;
  }
  .lbbp-badge-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #fbbf24;
    animation: lbbp-pulse 2s infinite;
    box-shadow: 0 0 8px rgba(251,191,36,0.6);
  }
  @keyframes lbbp-pulse {
    0%,100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(1.2); }
  }
  .lbbp-hero-title {
    font-size: clamp(20px, 4vw, 28px);
    font-weight: 800;
    color: #ffffff;
    line-height: 1.2;
    margin-bottom: 6px;
    text-shadow: 1px 1px 3px rgba(0,0,0,0.2);
  }
  .lbbp-hero-sub {
    font-size: 12px;
    font-weight: 500;
    color: rgba(255,255,255,0.85);
  }
  .lbbp-hero-id {
    font-size: 12px;
    font-weight: 700;
    color: #fcd34d;
    margin-top: 8px;
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  /* Membership Badges */
  .mem-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 14px;
    border-radius: 9px;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    background: rgba(255,255,255,0.1);
    backdrop-filter: blur(4px);
    border: 1px solid rgba(255,255,255,0.2);
  }
  .mem-badge.amber { color: #4ade80; }
  .mem-badge.amber2 { color: #f87171; }
  .mem-badge-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
    animation: mem-pulse 2s infinite;
  }
  @keyframes mem-pulse {
    0%,100% { opacity: 1; }
    50% { opacity: 0.4; }
  }

  .lbbp-hero-stats {
    display: flex;
    gap: 12px;
    position: relative;
    z-index: 1;
    flex-wrap: wrap;
  }
  .lbbp-hero-stat {
    background: rgba(255,255,255,0.1);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255,255,255,0.2);
    border-radius: 9px;
    padding: 12px 16px;
    text-align: center;
    min-width: 110px;
    transition: all 0.2s;
    box-shadow: 4px 4px 10px rgba(0,0,0,.1), -2px -2px 8px rgba(255,255,255,.1);
  }
  .lbbp-hero-stat:hover {
    transform: translateY(-3px);
    background: rgba(255,255,255,0.2);
  }
  .lbbp-hero-stat-val {
    font-size: clamp(16px, 2.5vw, 20px);
    font-weight: 900;
    background: linear-gradient(135deg, #fbbf24, #f59e0b);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  .lbbp-hero-stat-label {
    font-size: 9px;
    font-weight: 800;
    color: rgba(255,255,255,0.8);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-top: 4px;
  }

  /* ══════════════════════════════
     SECTION LABEL
  ══════════════════════════════ */
  .lbbp-section-label {
    font-size: 12px;
    font-weight: 800;
    background: linear-gradient(135deg, #2563eb, #8b5cf6);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .lbbp-section-label::after {
    content: '';
    flex: 1;
    height: 2px;
    background: linear-gradient(90deg, #2563eb, #8b5cf6, transparent);
    border-radius: 99px;
  }

  /* ══════════════════════════════
     METRICS GRID — Neumorphism Cards
  ══════════════════════════════ */
  .lbbp-metrics {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
  @media (max-width: 900px) { .lbbp-metrics { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 480px) { .lbbp-metrics { grid-template-columns: repeat(2, 1fr); } }

  .lbbp-metric-card {
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    border-radius: 9px;
    padding: 12px 14px;
    box-shadow: 8px 8px 14px #8f95a0, -5px -5px 10px #ffffff;
    display: flex;
    flex-direction: column;
    gap: 10px;
    position: relative;
    overflow: hidden;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .lbbp-metric-card:hover {
    transform: translateY(-2px);
    box-shadow: 12px 12px 22px #8f95a0, -6px -6px 14px #ffffff;
  }
  .lbbp-metric-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    border-radius: 28px 28px 0 0;
  }
  .lbbp-metric-card.blue::before   { background: linear-gradient(90deg, #3b82f6, #60a5fa); }
  .lbbp-metric-card.green::before  { background: linear-gradient(90deg, #10b981, #34d399); }
  .lbbp-metric-card.amber::before  { background: linear-gradient(90deg, #f59e0b, #fbbf24); }
  .lbbp-metric-card.purple::before { background: linear-gradient(90deg, #8b5cf6, #a78bfa); }

  .lbbp-metric-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .lbbp-metric-label {
    font-size: 11px;
    font-weight: 800;
    background: linear-gradient(135deg, #2563eb, #8b5cf6);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }
  .lbbp-metric-icon {
    width: 36px;
    height: 36px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: inset -2px -2px 5px rgba(255,255,255,0.7), inset 2px 2px 5px rgba(0,0,0,0.08);
  }
  .lbbp-metric-icon.blue   { background: linear-gradient(145deg, #dbeafe, #eff6ff); color: #3b82f6; }
  .lbbp-metric-icon.green  { background: linear-gradient(145deg, #dcfce7, #f0fdf4); color: #10b981; }
  .lbbp-metric-icon.amber  { background: linear-gradient(145deg, #fef3c7, #fffbeb); color: #f59e0b; }
  .lbbp-metric-icon.purple { background: linear-gradient(145deg, #ede9fe, #f5f3ff); color: #8b5cf6; }
  .lbbp-metric-val {
    font-size: clamp(18px, 2.5vw, 22px);
    font-weight: 900;
    color: #1e293b;
  }
  .lbbp-metric-unit {
    font-size: 10px;
    font-weight: 700;
    color: #94a3b8;
    margin-top: 4px;
  }

  /* ══════════════════════════════
     ROYALTY CARD — Neumorphism
  ══════════════════════════════ */
  .lbbp-royalty {
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    border-radius: 9px;
    padding: 18px 18px 16px;
    box-shadow: 10px 10px 18px #8f95a0, -6px -6px 12px #ffffff;
    position: relative;
    overflow: hidden;
  }
  .lbbp-royalty::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #f59e0b, #fbbf24, #fcd34d);
    border-radius: 24px 24px 0 0;
  }

  .lbbp-royalty-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 18px;
  }
  .lbbp-royalty-title {
    font-size: 13px;
    font-weight: 900;
    background: linear-gradient(135deg, #2563eb, #8b5cf6);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .lbbp-royalty-title-bar {
    width: 6px;
    height: 20px;
    border-radius: 99px;
    background: linear-gradient(180deg, #fbbf24, #f59e0b);
    box-shadow: 0 2px 8px rgba(245,158,11,0.4);
  }
  .lbbp-tier-pill {
    padding: 6px 14px;
    border-radius: 99px;
    font-size: 11px;
    font-weight: 800;
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    box-shadow: 4px 4px 8px #8f95a0, -2px -2px 6px #ffffff;
    color: #f59e0b;
    white-space: nowrap;
  }
  .lbbp-tier-pill.none {
    color: #94a3b8;
  }

  .lbbp-royalty-stats {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
    margin-bottom: 18px;
  }
  @media (max-width: 640px) { .lbbp-royalty-stats { grid-template-columns: 1fr; } }

  .lbbp-rstat {
    background: linear-gradient(145deg, #f0f4f9, #e8edf5);
    border-radius: 9px;
    padding: 14px;
    box-shadow: inset 2px 2px 5px rgba(0,0,0,0.05), inset -2px -2px 5px rgba(255,255,255,0.7);
    transition: all 0.2s;
  }
  .lbbp-rstat:hover {
    transform: translateY(-1px);
    box-shadow: 6px 6px 12px #8f95a0, -3px -3px 8px #ffffff;
  }
  .lbbp-rstat-label {
    font-size: 10px;
    font-weight: 800;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 8px;
  }
  .lbbp-rstat-val {
    font-size: clamp(16px, 2.2vw, 20px);
    font-weight: 900;
  }
  .lbbp-rstat-val.amber  { color: #f59e0b; }
  .lbbp-rstat-val.blue   { color: #3b82f6; }
  .lbbp-rstat-val.green  { color: #10b981; }
  .lbbp-rstat-check {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 99px;
    font-size: 11px;
    font-weight: 800;
    margin-top: 8px;
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    box-shadow: inset 1px 1px 2px rgba(255,255,255,0.7), inset -1px -1px 2px rgba(0,0,0,0.05);
  }
  .lbbp-rstat-check.met  { color: #10b981; }
  .lbbp-rstat-check.miss { color: #ef4444; }

  /* Rule Grid */
  .lbbp-rule-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 20px;
  }
  @media (max-width: 540px) { .lbbp-rule-grid { grid-template-columns: 1fr; } }

  .lbbp-rule-box {
    background: linear-gradient(145deg, #f0f4f9, #e8edf5);
    border-radius: 9px;
    padding: 18px;
    box-shadow: inset 2px 2px 5px rgba(0,0,0,0.05), inset -2px -2px 5px rgba(255,255,255,0.7);
  }
  .lbbp-rule-title {
    font-size: 13px;
    font-weight: 800;
    color: #1e293b;
    margin-bottom: 10px;
  }
  .lbbp-rule-progress-track {
    height: 8px;
    background: linear-gradient(145deg, #dce1e8, #e8edf5);
    border-radius: 99px;
    overflow: hidden;
    margin-bottom: 8px;
    box-shadow: inset 1px 1px 2px rgba(0,0,0,0.1), inset -1px -1px 2px rgba(255,255,255,0.5);
  }
  .lbbp-rule-progress-fill {
    height: 100%;
    border-radius: 99px;
    background: linear-gradient(90deg, #fbbf24, #f59e0b);
    transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .lbbp-rule-progress-fill.full { background: linear-gradient(90deg, #10b981, #059669); }
  .lbbp-rule-amounts {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    font-weight: 700;
    color: #64748b;
    margin-bottom: 8px;
  }
  .lbbp-rule-status {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    font-weight: 800;
    padding: 4px 12px;
    border-radius: 99px;
    margin-top: 8px;
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    box-shadow: inset 1px 1px 2px rgba(255,255,255,0.7), inset -1px -1px 2px rgba(0,0,0,0.05);
  }
  .lbbp-rule-status.met  { color: #10b981; }
  .lbbp-rule-status.miss { color: #ef4444; }

  /* Reason Banner */
  .lbbp-reason-banner {
    background: linear-gradient(145deg, #fef2f2, #fee2e2);
    border: 1px solid #fecaca;
    border-radius: 16px;
    padding: 14px 18px;
    font-size: 13px;
    font-weight: 700;
    color: #dc2626;
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 20px;
  }

  /* Tier Cards */
  .lbbp-tier-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }
  @media (max-width: 640px) { .lbbp-tier-grid { grid-template-columns: 1fr; } }

  .lbbp-tier-card {
    background: linear-gradient(145deg, #f0f4f9, #e8edf5);
    border-radius: 9px;
    padding: 20px;
    box-shadow: 8px 8px 16px #8f95a0, -4px -4px 10px #ffffff;
    transition: all 0.2s;
  }
  .lbbp-tier-card.current {
    background: linear-gradient(145deg, #fffbeb, #fef3c7);
    box-shadow: 0 0 0 2px #f59e0b, 12px 12px 24px #8f95a0;
  }
  .lbbp-tier-name {
    font-size: 15px;
    font-weight: 800;
    background: linear-gradient(135deg, #2563eb, #8b5cf6);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-transform: capitalize;
    margin-bottom: 12px;
  }
  .lbbp-tier-detail {
    font-size: 12px;
    font-weight: 700;
    color: #64748b;
    padding: 6px 0;
    display: flex;
    align-items: center;
    gap: 8px;
    border-bottom: 1px solid rgba(0,0,0,0.05);
  }
  .lbbp-tier-detail:last-of-type { border-bottom: none; }
  .lbbp-tier-royalty {
    font-size: 14px;
    font-weight: 900;
    color: #1e293b;
    margin-top: 10px;
  }
  .lbbp-eligible-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 12px;
    padding: 6px 14px;
    border-radius: 99px;
    background: linear-gradient(135deg, #f59e0b, #fbbf24);
    font-size: 11px;
    font-weight: 800;
    color: #0f172a;
    box-shadow: 0 2px 8px rgba(245,158,11,0.3);
  }

  /* ══════════════════════════════
     TWO-COL GRID
  ══════════════════════════════ */
  .lbbp-two-col {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
  }
  @media (max-width: 768px) { .lbbp-two-col { grid-template-columns: 1fr; } }

  /* ══════════════════════════════
     SHARED CARD
  ══════════════════════════════ */
  .lbbp-card {
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    border-radius: 9px;
    padding: 26px;
    box-shadow: 14px 14px 28px #8f95a0, -8px -8px 16px #ffffff;
  }
  .lbbp-card-title {
    font-size: 14px;
    font-weight: 900;
    background: linear-gradient(135deg, #2563eb, #8b5cf6);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: 22px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .lbbp-card-title-bar {
    width: 6px;
    height: 22px;
    border-radius: 99px;
    background: linear-gradient(180deg, #fbbf24, #f59e0b);
    box-shadow: 0 2px 8px rgba(245,158,11,0.4);
  }

  /* Income Rows */
  .lbbp-income-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border-radius: 16px;
    background: linear-gradient(145deg, #f0f4f9, #e8edf5);
    box-shadow: inset 2px 2px 4px rgba(0,0,0,0.04), inset -2px -2px 4px rgba(255,255,255,0.6);
    margin-bottom: 10px;
    transition: all 0.2s;
  }
  .lbbp-income-row:hover {
    transform: translateX(4px);
  }
  .lbbp-income-label {
    font-size: 13px;
    font-weight: 700;
    color: #475569;
  }
  .lbbp-income-amount {
    font-size: 15px;
    font-weight: 800;
  }
  .lbbp-income-total {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 18px;
    border-radius: 18px;
    margin-top: 8px;
    background: linear-gradient(135deg, #1e3a5f, #2563eb);
    box-shadow: 8px 8px 16px #8f95a0, -4px -4px 10px #ffffff;
  }
  .lbbp-income-total-label {
    font-size: 13px;
    font-weight: 800;
    color: rgba(255,255,255,0.9);
  }
  .lbbp-income-total-val {
    font-size: 22px;
    font-weight: 900;
    color: #fbbf24;
  }

  /* Wallet Rows */
  .lbbp-wallet-row {
    padding: 16px;
    border-radius: 16px;
    background: linear-gradient(145deg, #f0f4f9, #e8edf5);
    box-shadow: inset 2px 2px 4px rgba(0,0,0,0.04), inset -2px -2px 4px rgba(255,255,255,0.6);
    margin-bottom: 12px;
    transition: all 0.2s;
  }
  .lbbp-wallet-row:hover {
    transform: translateX(4px);
  }
  .lbbp-wallet-row-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .lbbp-wallet-row-label {
    font-size: 13px;
    font-weight: 700;
    color: #475569;
  }
  .lbbp-wallet-row-amount {
    font-size: 16px;
    font-weight: 800;
    color: #1e293b;
  }
  .lbbp-wallet-row-amount.total { color: #8b5cf6; font-size: 18px; }
  .lbbp-wallet-pending {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin-top: 8px;
    padding: 4px 12px;
    border-radius: 99px;
    background: linear-gradient(145deg, #fff3ea, #ffe4d6);
    font-size: 11px;
    font-weight: 800;
    color: #ea580c;
  }

  /* Action Buttons Grid */
  .lbbp-actions-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
  }
  @media (max-width: 800px) { .lbbp-actions-grid { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 480px) { .lbbp-actions-grid { grid-template-columns: 1fr; } }

  .lbbp-action-btn {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px 20px;
    border-radius: 20px;
    border: none;
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    box-shadow: 8px 8px 16px #8f95a0, -4px -4px 10px #ffffff;
    cursor: pointer;
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
    font-size: 13px;
    color: #1e293b;
    transition: all 0.2s ease;
    width: 100%;
  }
  .lbbp-action-btn:hover {
    transform: translateY(-2px);
    box-shadow: 12px 12px 20px #8f95a0, -6px -6px 14px #ffffff;
  }
  .lbbp-action-icon {
    width: 44px;
    height: 44px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .lbbp-action-btn:nth-child(1) .lbbp-action-icon { background: linear-gradient(135deg, #1e3a5f, #3b82f6); color: white; }
  .lbbp-action-btn:nth-child(2) .lbbp-action-icon { background: linear-gradient(135deg, #064e3b, #10b981); color: white; }
  .lbbp-action-btn:nth-child(3) .lbbp-action-icon { background: linear-gradient(135deg, #4c1d95, #8b5cf6); color: white; }
  .lbbp-action-btn:nth-child(4) .lbbp-action-icon { background: linear-gradient(135deg, #78350f, #f59e0b); color: white; }

  /* Skeleton Loading */
  .lbbp-skeleton {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 20px;
    max-width: 1280px;
    margin: 0 auto;
  }
  .lbbp-skel {
    border-radius: 32px;
    background: #dce1e8;
    position: relative;
    overflow: hidden;
    box-shadow: 8px 8px 16px #8f95a0, -8px -8px 16px #ffffff;
  }
  .lbbp-skel::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent 25%, rgba(255,255,255,0.5) 50%, transparent 75%);
    background-size: 200% 100%;
    animation: lbbp-shimmer 1.4s infinite;
  }
  @keyframes lbbp-shimmer {
    from { background-position: -200% 0; }
    to { background-position: 200% 0; }
  }
  .lbbp-skel-hero { height: 180px; }
  .lbbp-skel-row {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
  }
  @media (max-width: 900px) { .lbbp-skel-row { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 480px) { .lbbp-skel-row { grid-template-columns: 1fr; } }
  .lbbp-skel-box {
    height: 120px;
    border-radius: 28px;
    background: #dce1e8;
    position: relative;
    overflow: hidden;
  }
  .lbbp-skel-box::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent 25%, rgba(255,255,255,0.5) 50%, transparent 75%);
    background-size: 200% 100%;
    animation: lbbp-shimmer 1.4s infinite;
  }
  .lbbp-skel-card { height: 240px; }

  /* Error State */
  .lbbp-error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 40px;
    text-align: center;
    gap: 20px;
    background: linear-gradient(135deg, #1e3a5f, #2563eb);
    border-radius: 40px;
    margin: 40px;
    box-shadow: 20px 20px 40px #8f95a0, -10px -10px 20px #ffffff;
  }
  .lbbp-error-icon { font-size: 64px; }
  .lbbp-error-text {
    font-size: 18px;
    font-weight: 700;
    color: #ffffff;
  }
`;

/* ═══════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════ */
const LBBPDashboard = ({ user, setActivePage }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const dashRes = await axios.get(`${process.env.REACT_APP_API_URL}/dashboard/lbbp-partner`, { headers });
        setData(dashRes.data);
      } catch (err) {
        console.error("Failed to fetch LBBP dashboard data:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return (
    <div className="lbbp-root">
      <style>{styles}</style>
      <div className="lbbp-skeleton">
        <div className="lbbp-skel lbbp-skel-hero" />
        <div className="lbbp-skel-row">
          {[1,2,3,4].map(i => <div className="lbbp-skel-box" key={i} />)}
        </div>
        <div className="lbbp-skel lbbp-skel-card" />
        <div className="lbbp-skel lbbp-skel-card" />
      </div>
    </div>
  );

  if (!data) return (
    <div className="lbbp-root">
      <style>{styles}</style>
      <div className="lbbp-error">
        <div className="lbbp-error-icon">😕</div>
        <div className="lbbp-error-text">Unable to load LBBP dashboard</div>
      </div>
    </div>
  );

  return (
    <div className="lbbp-root">
      <style>{styles}</style>
      <div className="lbbp-page">

        {/* ── Hero Section ── */}
        <div className="lbbp-hero">
          <div className="lbbp-hero-left">
            <div className="lbbp-hero-badge">
              <div className="lbbp-badge-dot" /> LBBP Partner
            </div>
            <div className="lbbp-hero-title">Loan-Based Business Partnership</div>
            <div className="lbbp-hero-sub">Long-term Business Building Program</div>
            <div className="lbbp-hero-id">
              Unique ID: {data.user.uniqueId}
              {user?.hasSharePurchase ? (
                <span className="mem-badge amber">
                  <span className="mem-badge-dot" /> LBBP Active
                </span>
              ) : (
                <span className="mem-badge amber2">
                  <span className="mem-badge-dot" /> LBBP Inactive
                </span>
              )}
            </div>
          </div>
          <div className="lbbp-hero-stats">
            <div className="lbbp-hero-stat">
              <div className="lbbp-hero-stat-val">
                ₹{data.income.totalIncome.toLocaleString("en-IN")}
              </div>
              <div className="lbbp-hero-stat-label">Total LBBP Income</div>
            </div>
            <div className="lbbp-hero-stat">
              <div className="lbbp-hero-stat-val">
                ₹{((data.income.totalIncome || 0) - (data.wallet.totalWithdrawn || 0)).toLocaleString("en-IN")}
              </div>
              <div className="lbbp-hero-stat-label">Remaining Balance</div>
            </div>
            <div className="lbbp-hero-stat">
              <div className="lbbp-hero-stat-val">
                ₹{(data.wallet.eWallet || 0).toLocaleString("en-IN")}
              </div>
              <div className="lbbp-hero-stat-label">E-Wallet Balance</div>
            </div>
            <div className="lbbp-hero-stat">
              <div className="lbbp-hero-stat-val">
                ₹{(data.wallet.totalWithdrawn || 0).toLocaleString("en-IN")}
              </div>
              <div className="lbbp-hero-stat-label">Total Withdrawn</div>
            </div>
          </div>
        </div>

        {/* ── Income Breakdown ── */}
        <div className="lbbp-section-label">
          <FiTrendingUp size={14} /> Finance
        </div>
        <div className="lbbp-two-col">
          <div className="lbbp-card">
            <div className="lbbp-card-title">
              <div className="lbbp-card-title-bar" /> Income Breakdown
            </div>
            <IncomeRow label="Level Income" amount={data.income.levelIncome} color="#3b82f6" />
            <IncomeRow label="Royalty Income" amount={data.income.royaltyIncome} color="#10b981" />
            <IncomeRow label="EMI Income" amount={data.income.roiIncome} color="#f59e0b" />
            <div className="lbbp-income-total">
              <span className="lbbp-income-total-label">Total Income</span>
              <span className="lbbp-income-total-val">₹{data.income.totalIncome?.toLocaleString("en-IN")}</span>
            </div>
          </div>

          <div className="lbbp-card">
            <div className="lbbp-card-title">
              <div className="lbbp-card-title-bar" /> Wallet Status
            </div>
            <WalletRow label="LBBP Wallet" amount={data.income.totalIncome} pending={data.wallet.pendingWithdrawal} />
            <WalletRow label="E-Wallet" amount={data.wallet.eWallet} />
            <WalletRow label="Total Withdrawn" amount={data.wallet.totalWithdrawn} isTotal />
          </div>
        </div>

        {/* ── Quick Actions ── */}
        {/* <div className="lbbp-section-label">
          <FiZap size={14} /> Quick Actions
        </div>
        <div className="lbbp-card">
          <div className="lbbp-card-title">
            <div className="lbbp-card-title-bar" /> What would you like to do?
          </div>
          <div className="lbbp-actions-grid">
            <ActionBtn icon={<FiTrendingUp size={20} />} label="GCP Loan Pay" onClick={() => nav("buy-share")} />
            <ActionBtn icon={<FiDollarSign size={20} />} label="Wallet" onClick={() => nav("wallet")} />
            <ActionBtn icon={<FiUsers size={20} />} label="View Team" onClick={() => nav("team")} />
            <ActionBtn icon={<FiAward size={20} />} label="Royalty History" onClick={() => nav("royalty-history")} />
          </div>
        </div> */}

      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   SUB-COMPONENTS
═══════════════════════════════════════════ */
const IncomeRow = ({ label, amount, color }) => (
  <div className="lbbp-income-row">
    <span className="lbbp-income-label">{label}</span>
    <span className="lbbp-income-amount" style={{ color }}>
      ₹{amount?.toLocaleString("en-IN")}
    </span>
  </div>
);

const WalletRow = ({ label, amount, pending, isTotal }) => (
  <div className="lbbp-wallet-row">
    <div className="lbbp-wallet-row-top">
      <span className="lbbp-wallet-row-label" style={isTotal ? { fontWeight: 800, color: "#1e293b" } : {}}>
        {label}
      </span>
      <span className={`lbbp-wallet-row-amount ${isTotal ? "total" : ""}`}>
        ₹{amount?.toLocaleString("en-IN")}
      </span>
    </div>
    {pending > 0 && (
      <div className="lbbp-wallet-pending">
        <FiClock size={12} /> Pending: ₹{pending?.toLocaleString("en-IN")}
      </div>
    )}
  </div>
);

export default LBBPDashboard;